import axios from 'axios';
import { ReturnCodes } from '../utils/constants';
import fileSystemAccessService from './FileSystemAccessService';

class UserService {
    /**
     * Determine whether to use MongoDB API (v2) or SQL API (v1)
     * @returns {boolean} True if MongoDB should be used
     */
    shouldUseMongoDB() {
        return process.env.REACT_APP_USE_MONGODB === 'true' || 
               localStorage.getItem('userExperience') === 'new' ||
               fileSystemAccessService.shouldUseMongoDBForVideos();
    }

    /**
     * Determine whether to use enhanced user experience API (v2) or classic API (v1)
     * @returns {boolean} True if enhanced user experience should be used
     */
    shouldUseEnhancedUser() {
        return process.env.REACT_APP_USE_ENHANCED_USER === 'true' || 
               localStorage.getItem('userExperience') === 'new';
    }

    /**
     * Get all users from the system
     * @returns {Promise} Promise resolving to array of users
     */
    async getAllUsers(email, auth) {
        return new Promise(async (resolve, reject) => {
            try {
                const useMongoDb = this.shouldUseMongoDB();
                const apiBaseUrl = process.env.REACT_APP_API_URL;
                const apiEndpoint = useMongoDb 
                    ? `${apiBaseUrl}/api/v2/users`
                    : `${apiBaseUrl}/api/v1/users`;

                await axios
                    .get(apiEndpoint, {
                        headers: {
                            'Access-Control-Allow-Origin': '*',
                            'Access-Control-Allow-Credentials': true,
                            email: email,
                            auth: auth, // Use 'auth' consistently
                        },
                    })
                    .then(response => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(ReturnCodes.UNABLE_TO_GET_USERS);
                    });
            } catch (error) {
                reject(ReturnCodes.UNABLE_TO_GET_USERS);
            }
        });
    }

    /**
     * Login user with email and password
     * @param {string} email - User's email
     * @param {string} password - User's password
     * @returns {Promise} Promise resolving to login response
     */
    async login(email, password) {
        return new Promise(async (resolve, reject) => {
            try {
                const useMongoDb = this.shouldUseMongoDB();
                const apiBaseUrl = process.env.REACT_APP_API_URL;
                const apiEndpoint = useMongoDb 
                    ? `${apiBaseUrl}/api/v2/login`
                    : `${apiBaseUrl}/api/v1/login`;

                await axios
                    .post(apiEndpoint, {
                        email: email,
                        password: password,
                    })
                    .then(response => {
                        resolve(response);
                    })
                    .catch((error) => {
                        reject(ReturnCodes.FAILED);
                    });
            } catch (error) {
                reject(ReturnCodes.FAILED);
            }
        });
    }

    /**
     * Handle API errors
     * @param {Error} error - Error object from API call
     * @returns {ReturnCodes} Appropriate return code for the error
     */
    handleError(error) {
        if (error instanceof Error && error.response && error.response.data) {
            const errorMessage = error.response.data.message || '';
            
            if (errorMessage.includes("Duplicate entry") || 
                errorMessage.includes("already exists")) {
                console.log("Duplicate entry error detected.");
                return ReturnCodes.DUPLICATE_EMAIL;
            } else {
                console.log("Other error detected:", errorMessage);
                return ReturnCodes.FAILED;
            }
        } else {
            console.log("Unknown error format:", error);
            return ReturnCodes.FAILED;
        }
    }

    /**
     * Register new user
     * @param {string} firstName - User's first name
     * @param {string} lastName - User's last name
     * @param {string} email - User's email
     * @param {string} password - User's password
     * @returns {Promise} Promise resolving to created user
     */
    async register(firstName, lastName, email, password, userType = 'user', createdBy = null, notificationEmail) {
        const authUserEmail = JSON.parse(localStorage.getItem('authUser')).email;
        const authToken = JSON.parse(localStorage.getItem('authToken'));

        const useMongoDb = this.shouldUseMongoDB();
                const apiBaseUrl = process.env.REACT_APP_API_URL;
                const apiEndpoint = useMongoDb 
                    ? `${apiBaseUrl}/api/v2/users`
                    : `${apiBaseUrl}/api/v1/users`;

        return axios
                .post(apiEndpoint, 
                    {
                        firstName,
                        lastName,
                        email,
                        password,
                        userType,
                        createdBy, // Add this field to the request
                        notificationEmail
                    },
                    {
                        headers: {
                            'Access-Control-Allow-Origin': '*',
                            'Access-Control-Allow-Credentials': true,
                            email: authUserEmail,
                            auth: authToken,
                        },
                    }
        );
    }

    /**
     * Get user by email
     * @param {string} userEmail - Email of the user to retrieve
     * @param {string} email - Current user's email for auth
     * @param {string} auth - Current user's auth token
     * @returns {Promise} Promise resolving to user data
     */
    async getUserByEmail(userEmail, email, auth) {
        return new Promise(async (resolve, reject) => {
            try {
                const useMongoDb = this.shouldUseMongoDB();
                const apiBaseUrl = process.env.REACT_APP_API_URL;
                const apiEndpoint = useMongoDb 
                    ? `${apiBaseUrl}/api/v2/user/${userEmail}`
                    : `${apiBaseUrl}/api/v1/user/${userEmail}`;

                await axios
                    .get(apiEndpoint, {
                        headers: {
                            'Access-Control-Allow-Origin': '*',
                            'Access-Control-Allow-Credentials': true,
                            email: email,
                            auth: auth,
                        },
                    })
                    .then(response => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(ReturnCodes.UNABLE_TO_GET_USER);
                    });
            } catch (error) {
                reject(ReturnCodes.UNABLE_TO_GET_USER);
            }
        });
    }

    /**
     * Update an existing user
     * @param {string} userEmail - Email of the user to update
     * @param {object} userData - Updated user data
     * @param {string} email - Current user's email for auth
     * @param {string} auth - Current user's auth token
     * @returns {Promise} Promise resolving to updated user
     */
    async updateUser(userEmail, userData, email, auth) {
        const authUserEmail = JSON.parse(localStorage.getItem('authUser')).email;
        const authToken = JSON.parse(localStorage.getItem('authToken'));
        return new Promise(async (resolve, reject) => {
            try {
                const useMongoDb = this.shouldUseMongoDB();
                const apiBaseUrl = process.env.REACT_APP_API_URL;
                const apiEndpoint = useMongoDb 
                    ? `${apiBaseUrl}/api/v2/user/${userEmail}`
                    : `${apiBaseUrl}/api/v1/user/${userEmail}`;

                await axios
                    .put(apiEndpoint, userData, {
                        headers: {
                            'Access-Control-Allow-Origin': '*',
                            'Access-Control-Allow-Credentials': true,
                            email: authUserEmail,
                            auth: authToken,
                        },
                    })
                    .then(response => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(ReturnCodes.FAILED);
                    });
            } catch (error) {
                reject(ReturnCodes.FAILED);
            }
        });
    }

    /**
     * Delete a user by email
     * @param {string} userEmail - Email of the user to delete
     * @param {string} email - Current user's email for auth
     * @param {string} auth - Current user's auth token
     * @returns {Promise} Promise resolving to deletion status
     */
    async deleteUser(userEmail, email, auth) {
        return new Promise(async (resolve, reject) => {
            try {
                const useMongoDb = this.shouldUseMongoDB();
                const apiBaseUrl = process.env.REACT_APP_API_URL;
                const apiEndpoint = useMongoDb 
                    ? `${apiBaseUrl}/api/v2/user/${userEmail}`
                    : `${apiBaseUrl}/api/v1/user/${userEmail}`;

                await axios
                    .delete(apiEndpoint, {
                        headers: {
                            'Access-Control-Allow-Origin': '*',
                            'Access-Control-Allow-Credentials': true,
                            email: email,
                            auth: auth,
                        },
                    })
                    .then(response => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(ReturnCodes.FAILED);
                    });
            } catch (error) {
                reject(ReturnCodes.FAILED);
            }
        });
    }

    // Change user password
    async changePassword(currentPassword, newPassword) {
        const useMongoDb = this.shouldUseMongoDB();
        const apiBaseUrl = process.env.REACT_APP_API_URL;
        const apiEndpoint = '/api/v2/users/change-password';

        const authUser = JSON.parse(localStorage.getItem('authUser')).email;
        const authToken = JSON.parse(localStorage.getItem('authToken'));
        
        return axios.post(`${apiBaseUrl}${apiEndpoint}`, {
            authUser,
            currentPassword,
            newPassword
        }, {
            headers: {
                'email': authUser,
                'auth': authToken,
                'Access-Control-Allow-Origin': '*', 
                'Access-Control-Allow-Credentials': true
            }
        });
    }

    /**
     * Create a new user
     * @param {object} userData - Data of the user to create
     * @param {string} adminEmail - Admin's email for auth
     * @param {string} authToken - Admin's auth token
     * @returns {Promise} Promise resolving to created user
     */
    async createUser(userData) {
        const useMongoDb = this.shouldUseMongoDB();
        const apiBaseUrl = process.env.REACT_APP_API_URL;

        const authUser = JSON.parse(localStorage.getItem('authUser')).email;
        const authToken = JSON.parse(localStorage.getItem('authToken'));
        const endpoint = useMongoDb ? '/api/v2/users' : '/api/v1/users';
        
        return axios.post(`${apiBaseUrl}${endpoint}`, userData, {
            headers: {
                'email': authUser,
                'auth': authToken,
                'Access-Control-Allow-Origin': '*', 
                'Access-Control-Allow-Credentials': true
            }
        });
    }

    /**
     * Register new user
     * @param {string} firstName - User's first name
     * @param {string} lastName - User's last name
     * @param {string} email - User's email
     * @param {string} password - User's password
     * @param {string} userType - Type of user (e.g., 'user', 'admin')
     * @param {string} createdBy - Email of the admin who created the user
     * @param {string} notificationEmail - Email for receiving notifications
     * @returns {Promise} Promise resolving to registration response
     */
    async register(firstName, lastName, email, password, userType = 'user', createdBy = null, notificationEmail) {
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/v2/user/register`,
                {
                    firstName,
                    lastName,
                    email,
                    password,
                    userType,
                    createdBy,
                    notificationEmail
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                    }
                }
            );
            return response;
        } catch (error) {
            console.error('Error registering user:', error);
            throw error;
        }
    }

    /**
     * Update an existing user
     * @param {string} userEmail - Email of the user to update
     * @param {object} userData - Updated user data
     * @param {string} adminEmail - Admin's email for auth
     * @param {string} adminToken - Admin's auth token
     * @returns {Promise} Promise resolving to updated user
     */
    async updateUser(userEmail, userData, adminEmail, adminToken) {
        try {
            const response = await axios.put(
                `${process.env.REACT_APP_API_URL}/api/v2/user/${userEmail}`,
                userData,
                {
                    headers: {
                        email: adminEmail,
                        auth: adminToken,
                        'Content-Type': 'application/json',
                    }
                }
            );
            return response;
        } catch (error) {
            console.error('Error updating user:', error);
            throw error;
        }
    }
}

const userServiceInstance = new UserService();
export default userServiceInstance;